import { randomString } from '@eigtech/function-utils'
import type {
  AddNoteBodyRequest,
  AddNoteResponse,
  AddNoteRequest as BaseAddNoteRequest,
  GetNotebookForCohortResponse,
  Note,
} from '@eigtech/notes-types'
import { Corn, getCornKernels } from '@eigtech/shared-corn'
import {
  contextualPost,
  isLastQueuedMutation,
  mutationOptions,
  type MutationScope,
  QueryClient,
  useMutation,
  useQueryClient,
} from '@eigtech/ui-shared-api'
import { notebooksBasePath, notebooksQueryKeys } from './constants'

export type AddNoteRequest = Omit<BaseAddNoteRequest, 'entityId'> & { entityCorn: Corn }

export const createEntityNoteMutationKey = ['notes', 'entityNote', 'create']

/**
 * Notes written offline are queued as paused mutations and resumed all at once on
 * reconnect. A shared scope posts them one at a time, in the order they were written,
 * so the notebook reads back in the order the user wrote it. Keyed by entity, like the
 * notebook query they write — notes on one claim have no reason to wait out a queue of
 * notes on another. `undefined` where the entity isn't known until submit (see
 * `useCreateOptionalEntityNote`) and for the mutation defaults restored mutations
 * resume from, which carry their own scope.
 */
const createEntityNoteMutationScope = (entityCorn: Corn | undefined): MutationScope => ({
  id: entityCorn ? `notes-entity-note-create:${entityCorn}` : 'notes-entity-note-create',
})

export const createEntityNoteMutationOptions = (
  queryClient: QueryClient,
  /** Scopes this note behind the others queued for the same entity. */
  entityCorn: Corn | undefined
) => {
  const scope = createEntityNoteMutationScope(entityCorn)

  return mutationOptions({
    mutationKey: createEntityNoteMutationKey,
    scope,
    mutationFn: async ({ entityCorn, ...request }: AddNoteRequest) => {
      const id = getCornKernels(entityCorn).resourceId
      await contextualPost<AddNoteResponse, AddNoteBodyRequest>(
        `${notebooksBasePath}/${id}`,
        request
      )
      return { entityCorn, entityId: id, ...request }
    },
    async onMutate(data: AddNoteRequest) {
      await queryClient.cancelQueries({
        queryKey: notebooksQueryKeys.entityNotebook(data.entityCorn),
      })

      const previousNotebook = queryClient.getQueryData(
        notebooksQueryKeys.entityNotebook(data.entityCorn)
      )

      queryClient.setQueryData<GetNotebookForCohortResponse>(
        notebooksQueryKeys.entityNotebook(data.entityCorn),
        (old) => {
          if (old === undefined) return old
          const tempNote: Note = {
            ...data,
            entityId: getCornKernels(data.entityCorn).resourceId,
            createdBy: 'User (pending)',
            createdOn: new Date().toISOString(),
            id: `corn:notes:note:${randomString()}`,
          }
          return {
            ...old,
            notes: [tempNote, ...(old.notes ?? [])],
          }
        }
      )

      return {
        previousNotebook,
      }
    },
    onError: (__, data, context) => {
      if (context === undefined) return
      queryClient.setQueryData(
        notebooksQueryKeys.entityNotebook(data.entityCorn),
        context.previousNotebook
      )
    },
    onSettled(data) {
      if (data === undefined) return
      // A refetch replaces the notebook wholesale, so one armed while notes are still
      // queued behind this one would drop their optimistic rows — nothing puts those
      // back but the posts themselves. Only the last note of a queue refetches.
      if (!isLastQueuedMutation(queryClient, scope)) return
      //Kinda gross
      setTimeout(
        () =>
          queryClient.invalidateQueries({
            queryKey: notebooksQueryKeys.entityNotebook(data.entityCorn),
          }),
        20000
      )
    },
  })
}

export function useCreateEntityNote(entityCorn?: Corn) {
  return useMutation(createEntityNoteMutationOptions(useQueryClient(), entityCorn))
}
